import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { getClientFullName } from "@zfp-akademie-a-s/contract-details";
import { Flex, HStack } from "@zfp-akademie-a-s/zfp-design";
import { AddIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import {
  type ContractProposalBaseSolutionHorizonType,
  type ContractProposalListItem,
  type CurrentContractListItem,
  CurrentContractListItemProposedStepActionType,
  type NsurePlanOverview,
} from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext } from "react";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { GoalFulfillmentSolutionItem } from "./GoalFulfillmentSolutionItem";
import { SolutionType } from "./types";

type Props = {
  horizonType: ContractProposalBaseSolutionHorizonType;
  onClickAdd: () => void;
  solutionType: SolutionType;
  nsurePlan?: NsurePlanOverview;
  contractProposals?: ContractProposalListItem[];
  currentContracts?: CurrentContractListItem[];
  isAddDisabled?: boolean;
};

export const GoalFulfillmentSolutions = ({
  horizonType,
  onClickAdd,
  solutionType,
  nsurePlan,
  contractProposals,
  currentContracts,
  isAddDisabled,
}: Props) => {
  const { t } = useResource();
  const { readOnly } = useContext(AnalysisDetailContext);

  const hasSolutions = solutionType === SolutionType.Proposed ? (contractProposals?.length ?? 0) > 0 : (currentContracts?.length ?? 0) > 0;

  const renderProposalItem = (proposal: ContractProposalListItem) => (
    <GoalFulfillmentSolutionItem
      key={proposal.guid}
      solutionType={solutionType}
      solutionID={proposal.guid}
      title={proposal.partner?.name ?? t(Resources.AnalysisDetail.GoalsFulfillment.InvestmentToolTypeOptions[proposal.investmentToolType])}
      product={proposal.portfolio?.name ?? proposal.productName ?? undefined}
      proposedSolutionAction={CurrentContractListItemProposedStepActionType.Contracting}
      clientName={getClientFullName({ name: proposal.client.firstName, surname: proposal.client.surname })}
      appreciationRate={proposal.appreciationRate}
      solutionHorizon={proposal.horizonYears}
      regularAmount={proposal.regularAmount.value}
      oneTimeAmount={proposal.oneTimeAmount.value}
      futureAmount={proposal.futureAmount.value}
      horizonType={horizonType}
      investmentToolType={proposal.investmentToolType}
      isNsure={proposal.portfolio !== null}
      nsurePlan={nsurePlan}
      portfolio={proposal.portfolio}
    />
  );

  const proposalsWithPortfolio = contractProposals?.filter((p) => p.portfolio) ?? [];
  const proposalsWithoutPortfolio = contractProposals?.filter((p) => !p.portfolio) ?? [];

  const proposedItems = [
    ...(proposalsWithPortfolio.length > 0
      ? [
          <Box key="portfolio-group" sx={{ border: "3px solid", borderColor: "primary.light", borderRadius: 2, overflow: "hidden" }}>
            <Flex sx={{ backgroundColor: "primary.light" }} px={Spacing.L} py={Spacing.L}>
              <Typography variant="body2" fontWeight={600}>
                {t(Resources.AnalysisDetail.GoalsFulfillment.PortfolioGroupTitle)}
              </Typography>
            </Flex>
            <Stack divider={<Divider />}>{proposalsWithPortfolio.map(renderProposalItem)}</Stack>
          </Box>,
        ]
      : []),
    ...proposalsWithoutPortfolio.map(renderProposalItem),
  ];

  return (
    <Stack
      spacing={Spacing.S}
      pb={Spacing.L}
      pt={Spacing.XS}
      px={Spacing.XL}
      sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3 }}
    >
      <HStack
        justifyContent={"space-between"}
        alignItems={"center"}
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          borderTopLeftRadius: 3,
          borderTopRightRadius: 3,
        }}
        py={Spacing.M}
      >
        <Typography variant="h3">{t(Resources.AnalysisDetail.GoalsFulfillment[solutionType])}</Typography>
        <Button
          variant="text"
          color={"primary"}
          sx={{ width: "fit-content" }}
          startIcon={<AddIcon />}
          onClick={onClickAdd}
          disabled={readOnly || isAddDisabled}
        >
          {t(Resources.AnalysisDetail.GoalsFulfillment.AddSolution)}
        </Button>
      </HStack>
      <Stack spacing={Spacing.S} divider={<Divider />}>
        {hasSolutions ? (
          solutionType === SolutionType.Proposed ? (
            proposedItems
          ) : (
            currentContracts?.map((contract) => {
              return (
                <GoalFulfillmentSolutionItem
                  key={contract.guid}
                  solutionType={solutionType}
                  solutionID={contract.guid}
                  title={
                    !!contract.partnerName
                      ? contract.partnerName
                      : t(Resources.AnalysisDetail.GoalsFulfillment.InvestmentToolTypeOptions[contract.investmentToolType])
                  }
                  proposedSolutionAction={contract.proposedStepActionType}
                  clientName={getClientFullName({ name: contract.client.firstName, surname: contract.client.surname })}
                  appreciationRate={contract.appreciationRate}
                  solutionHorizon={contract.horizonYears}
                  regularAmount={contract.regularAmount.value}
                  oneTimeAmount={contract.oneTimeAmount.value}
                  futureAmount={contract.futureAmount.value}
                  regularAmountChange={contract.regularAmountChange?.value ?? 0}
                  oneTimeAmountChange={contract.oneTimeAmountChange?.value ?? 0}
                  horizonType={horizonType}
                  investmentToolType={contract.investmentToolType}
                />
              );
            })
          )
        ) : (
          <Flex px={Spacing.L} pt={Spacing.L}>
            <Typography variant="body2" fontWeight={400} color="textSecondary">
              {t(Resources.AnalysisDetail.GoalsFulfillment.NoSolution)}
            </Typography>
          </Flex>
        )}
      </Stack>
    </Stack>
  );
};
